import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, Store } from "lucide-react";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useSections } from "@/lib/sections";

export type HeroSlide = { id: string; image: string; label_ar: string; label_en: string };
export type HeroCfg = { title_ar: string; title_en: string; subtitle_ar: string; subtitle_en: string; interval: number; slides: HeroSlide[] };

let cached: HeroCfg | null = null;

export const HeroView = ({ cfg, showPartner = true }: { cfg: HeroCfg; showPartner?: boolean }) => {
  const { t, lang } = usePrefs();
  const onlySpots = useSections().mode === "spots";
  const [i, setI] = useState(0);
  const n = cfg.slides.length;
  useEffect(() => {
    if (n < 2) return;
    const id = setTimeout(() => setI((x) => (x + 1) % n), cfg.interval * 1000);
    return () => clearTimeout(id);
  }, [i, n, cfg.interval]);
  const cur = cfg.slides[Math.min(i, n - 1)];
  const ar = lang === "ar";
  const pick = (a: string, e: string, key: string) => (ar ? a : e) || t(key);
  const label = ar ? cur?.label_ar : cur?.label_en || cur?.label_ar;
  const Arrow = ar ? ArrowLeft : ArrowRight;

  return (
    <section data-testid="hero-section" className="relative overflow-hidden rounded-[2rem] min-h-[440px] sm:min-h-[500px] flex items-end isolate bg-[#1F1914]">
      <AnimatePresence initial={false}>
        <motion.img key={cur?.id} src={cur?.image} alt="" data-testid="hero-image"
          initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 0.9 }, scale: { duration: cfg.interval + 1, ease: "linear" } }}
          className="absolute inset-0 h-full w-full object-cover -z-10" />
      </AnimatePresence>
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#1F1914] via-[#1F1914]/60 to-[#1F1914]/5" />
      <div className="relative w-full p-5 sm:p-10 text-white">
        <div className="max-w-2xl space-y-3 sm:space-y-4">
          <AnimatePresence mode="wait">
            {label && (
              <motion.span key={cur.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}
                data-testid="hero-label" className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 px-3 py-1 text-xs font-bold">
                <Sparkles className="h-3.5 w-3.5 text-amber-300" />{label}
              </motion.span>
            )}
          </AnimatePresence>
          <h1 data-testid="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] drop-shadow-sm">{pick(cfg.title_ar, cfg.title_en, "hero.title")}</h1>
          <p data-testid="hero-subtitle" className="text-sm sm:text-lg text-white/85 leading-relaxed max-w-xl">{pick(cfg.subtitle_ar, cfg.subtitle_en, "hero.subtitle")}</p>
          <div className="grid grid-cols-1 sm:flex gap-2 pt-2">
            <a href={onlySpots ? "#featured-spots" : "#explore"} data-testid="hero-explore-button" className="group h-12 px-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary font-bold shadow-lg shadow-primary/40 hover:bg-primary/90 active:scale-[0.97] transition-[transform,background-color]">
              {t(onlySpots ? "hero.ctaSpots" : "hero.cta")}<Arrow className="h-4 w-4 transition-transform group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5" />
            </a>
            {showPartner && (
              <Link to="/partner/register" data-testid="hero-partner-button" className="h-12 px-5 inline-flex items-center justify-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/25 font-bold text-sm hover:bg-white/20 transition-[background-color]">
                <Store className="h-4 w-4" />{t("hero.partner")}
              </Link>
            )}
          </div>
        </div>
        {n > 1 && (
          <div className="flex gap-1.5 mt-5" data-testid="hero-dots">
            {cfg.slides.map((s, k) => (
              <button key={s.id} onClick={() => setI(k)} aria-label={ar ? s.label_ar || `${k + 1}` : s.label_en || `${k + 1}`} data-testid={`hero-dot-${k}`}
                className="relative h-1.5 w-8 rounded-full bg-white/25 overflow-hidden">
                {k === i && <motion.span key={`${s.id}-${i}`} initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: cfg.interval, ease: "linear" }} className="absolute inset-y-0 start-0 bg-white rounded-full" />}
                {k < i && <span className="absolute inset-0 bg-white/70" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export const Hero = ({ showPartner = true }: { showPartner?: boolean }) => {
  const [cfg, setCfg] = useState<HeroCfg | null>(cached);
  useEffect(() => {
    api.get("/hero").then((r) => { cached = r.data; setCfg(r.data); }).catch(() => {});
  }, []);
  if (!cfg) return <section data-testid="hero-section" className="rounded-[2rem] min-h-[440px] sm:min-h-[500px] bg-[#1F1914] animate-pulse" />;
  return <HeroView cfg={cfg} showPartner={showPartner} />;
};
